import { makeAuthenticatedRequest } from './authService';
import {
  KnowledgeNode,
  NodeWithRelationships,
  KnowledgeEdge,
  KnowledgeAnswer,
  GraphData,
  CreateNodeData,
  UpdateNodeData,
  CreateEdgeData,
  AnswerReview,
  GenerateNodeResponse
} from '../components/sea-of-knowledge/types';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

class SeaOfKnowledgeService {
  // Get all nodes for user
  async getNodes(): Promise<{ success: boolean; data?: { nodes: KnowledgeNode[] }; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/sea-of-knowledge/nodes', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching nodes:', error);
      throw error;
    }
  }

  // Get single node with relationships
  async getNode(nodeId: number): Promise<{ success: boolean; data?: { node: NodeWithRelationships }; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/sea-of-knowledge/nodes/${nodeId}`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching node:', error);
      throw error;
    }
  }

  // Create new node
  async createNode(data: CreateNodeData): Promise<{ success: boolean; data?: { node: KnowledgeNode }; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/sea-of-knowledge/nodes', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return response;
    } catch (error) {
      console.error('Error creating node:', error);
      throw error;
    }
  }

  // Update node
  async updateNode(nodeId: number, data: UpdateNodeData): Promise<{ success: boolean; data?: { node: KnowledgeNode }; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/sea-of-knowledge/nodes/${nodeId}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
      return response;
    } catch (error) {
      console.error('Error updating node:', error);
      throw error;
    }
  }

  // Delete node
  async deleteNode(nodeId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/sea-of-knowledge/nodes/${nodeId}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error('Error deleting node:', error);
      throw error;
    }
  }

  // Generate node from context (based on existing node)
  async generateNodeFromContext(nodeId: number): Promise<{ success: boolean; data?: { node: KnowledgeNode }; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/sea-of-knowledge/nodes/${nodeId}/generate`, {
        method: 'POST'
      });
      return response;
    } catch (error) {
      console.error('Error generating node from context:', error);
      throw error;
    }
  }

  // Generate node from user prompt
  async generateNodeFromPrompt(prompt: string): Promise<{ success: boolean; data?: { node: KnowledgeNode }; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/sea-of-knowledge/nodes/generate', {
        method: 'POST',
        body: JSON.stringify({ prompt })
      });
      return response;
    } catch (error) {
      console.error('Error generating node from prompt:', error);
      throw error;
    }
  }

  // Create edge between nodes
  async createEdge(data: CreateEdgeData): Promise<{ success: boolean; data?: { edge: KnowledgeEdge }; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/sea-of-knowledge/edges', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      return response;
    } catch (error) {
      console.error('Error creating edge:', error);
      throw error;
    }
  }

  // Delete edge
  async deleteEdge(edgeId: number): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/sea-of-knowledge/edges/${edgeId}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error('Error deleting edge:', error);
      throw error;
    }
  }

  // Submit answer for node
  async submitAnswer(nodeId: number, answerText: string): Promise<{ success: boolean; data?: { answer: KnowledgeAnswer }; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/sea-of-knowledge/nodes/${nodeId}/answer`, {
        method: 'POST',
        body: JSON.stringify({ answer_text: answerText })
      });
      return response;
    } catch (error) {
      console.error('Error submitting answer:', error);
      throw error;
    }
  }

  // Get AI review of answer
  async reviewAnswer(nodeId: number, answerId?: number, answerText?: string): Promise<{ success: boolean; review?: string; score?: number; approved?: boolean; message?: string }> {
    try {
      const body: any = {};
      if (answerId) {
        body.answer_id = answerId;
      }
      if (answerText) {
        body.answer_text = answerText;
      }

      const response = await makeAuthenticatedRequest(`/sea-of-knowledge/nodes/${nodeId}/review`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      return response;
    } catch (error) {
      console.error('Error reviewing answer:', error);
      throw error;
    }
  }

  // Get answer history for node
  async getAnswers(nodeId: number): Promise<{ success: boolean; data?: { answers: KnowledgeAnswer[] }; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/sea-of-knowledge/nodes/${nodeId}/answers`, {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching answers:', error);
      throw error;
    }
  }

  // Get full graph data
  async getGraph(): Promise<{ success: boolean; data?: { nodes: KnowledgeNode[]; edges: KnowledgeEdge[] }; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/sea-of-knowledge/graph', {
        method: 'GET'
      });
      return response;
    } catch (error) {
      console.error('Error fetching graph:', error);
      throw error;
    }
  }
}

export const seaOfKnowledgeService = new SeaOfKnowledgeService();

